'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Map, X } from 'lucide-react';
import type { Continent } from '@/hooks/useContinents';

interface QuickCompareContinentsProps {
  continents: Continent[];
}

export default function QuickCompareContinents({ continents }: QuickCompareContinentsProps) {
  const router = useRouter();
  const [selectedContinents, setSelectedContinents] = useState<(Continent | undefined)[]>([undefined, undefined]);

  const handleContinentSelect = (continent: Continent, index: number) => {
    setSelectedContinents(prev => {
      const newSelection = [...prev];
      newSelection[index] = continent;
      return newSelection;
    });
  };

  const handleRemoveContinent = (index: number) => {
    setSelectedContinents(prev => {
      const newSelection = [...prev];
      newSelection[index] = undefined as any;
      return newSelection;
    });
  };

  const handleCompare = () => {
    if (selectedContinents[0] && selectedContinents[1]) {
      const url = `/map?a=${selectedContinents[0].slug}&b=${selectedContinents[1].slug}`;
      router.push(url);
    }
  };

  const canCompare = selectedContinents[0] && selectedContinents[1];

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Map className="h-4 w-4 text-blue-600" />
        <h2 className="text-base font-semibold text-slate-900">Quick Compare Continents</h2>
      </div>
      
      <p className="text-slate-600 mb-4 text-sm">
        Select two continents to compare their true size on the interactive map.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        {/* 第一个大洲选择 */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            First Continent
          </label>
          <select
            value={selectedContinents[0]?.id || ''}
            onChange={(e) => {
              const continent = continents.find(c => c.id === e.target.value);
              if (continent) handleContinentSelect(continent, 0);
            }}
            className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select continent...</option>
            {continents.map((continent) => (
              <option key={continent.id} value={continent.id}>
                {continent.name}
              </option>
            ))}
          </select>
          {selectedContinents[0] && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 rounded-md">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: selectedContinents[0].color }}
              />
              <span className="text-sm text-slate-700 flex-1">{selectedContinents[0].name}</span>
              <button
                onClick={() => handleRemoveContinent(0)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {/* 第二个大洲选择 */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Second Continent
          </label>
          <select
            value={selectedContinents[1]?.id || ''}
            onChange={(e) => {
              const continent = continents.find(c => c.id === e.target.value);
              if (continent) handleContinentSelect(continent, 1);
            }}
            className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select continent...</option>
            {continents.map((continent) => (
              <option key={continent.id} value={continent.id}>
                {continent.name}
              </option>
            ))}
          </select>
          {selectedContinents[1] && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 rounded-md">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: selectedContinents[1].color }}
              />
              <span className="text-sm text-slate-700 flex-1">{selectedContinents[1].name}</span>
              <button
                onClick={() => handleRemoveContinent(1)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleCompare}
        disabled={!canCompare}
        className={`w-full py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
          canCompare
            ? 'bg-sky-700 text-white hover:bg-sky-800'
            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
        }`}
      >
        <Map className="h-4 w-4" />
        Compare continents on map
      </button>
    </div>
  );
}
